import React from 'react';
import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiImage, FiShuffle } from 'react-icons/fi';
import { messageImageRefresh, urlMetaImageRecovery } from '../../util/imageRecovery';
import useImageRecovery from '../../hooks/useImageRecovery';

export const SIMILAR_COLOR_IMAGES = gql`
    query SimilarColorImages($id: ID!) {
        similarImageColors(id: $id) {
            score
            message {
                id title imageDisplayColors { hex proportion }
                image { url cdn_url name displayName thumbnail { md lg } }
                file { url cdn_url name displayName mime type }
                urlMeta { image image_cdn_url image_name public_id title gallery { type image image_name media { url cdn_url name } } }
            }
        }
    }
`;
// Keep fallbacks tied to the same asset rather than showing an unrelated gallery image.
const imageFor = message => {
    const image = message?.image ||
        (String(message?.file?.mime || message?.file?.type || '').startsWith('image/') ? message.file : null);
    let sources, refresh;
    if (image) {
        sources = [image.thumbnail?.md, image.thumbnail?.lg, image.cdn_url, image.url];
        refresh = messageImageRefresh(message.id, message.image ? 'image' : 'file');
    } else {
        const meta = message?.urlMeta?.find(meta => meta.image_cdn_url || meta.image || meta.image_name);
        const slide = !meta && message?.urlMeta?.flatMap(meta => meta.gallery || [])
            .find(slide => slide.type !== 'video' && (slide.image || slide.image_name || slide.media?.url || slide.media?.cdn_url));
        sources = meta ? [meta.image_cdn_url, meta.image] : [slide?.image, slide?.media?.cdn_url, slide?.media?.url];
        refresh = urlMetaImageRecovery(meta || slide).refresh;
    }
    return { sources: [...new Set(sources.filter(Boolean))], refresh };
};

function SimilarImageResult({ candidate, onOpen, label }) {
    const client = useApolloClient();
    const { sources, refresh } = imageFor(candidate);
    const { src, onError: handleError } = useImageRecovery({ sources, refresh, allowRefresh: true, client });
    if (!sources.length) return null;
    return <Result type="button" aria-label={label} onClick={() => onOpen?.(candidate,
        { type: 'image', src: src || sources[0], displayName: candidate.title || candidate.image?.displayName })}>
        {src ? <img src={src} alt={label} loading="lazy" onError={handleError} /> :
            <Placeholder><FiImage aria-hidden="true" /><span>{label}</span></Placeholder>}
    </Result>;
}

export default function SimilarColorImages({ message, onOpen }) {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [find, { data, loading, error, variables }] = useLazyQuery(SIMILAR_COLOR_IMAGES, { fetchPolicy: 'network-only' });
    React.useEffect(() => setOpen(false), [message?.id]);
    const items = variables?.id === message?.id ? data?.similarImageColors || [] : [];
    const search = () => { setOpen(true); void find({ variables: { id: message.id } }).catch(() => undefined); };
    return <>
        <Find type="button" onClick={search} disabled={loading} aria-expanded={open}>
            <FiShuffle aria-hidden="true" />{t('folders:findSimilarImages')}
        </Find>
        {open && <Results>
            <ResultHeader><span>{t('folders:similarImageColors')}</span><button type="button" onClick={() => setOpen(false)}
                aria-label={t('common:close')}>×</button></ResultHeader>
            {loading ? <p role="status">{t('folders:findingSimilarImages')}</p> : error ?
                <p role="alert">{t('folders:similarImagesUnavailable')}</p> : items.length ? <Grid>
                    {items.map(({ message: candidate }) => <SimilarImageResult key={candidate.id}
                        candidate={candidate} onOpen={onOpen} label={candidate.title || t('folders:imagePreview')} />)}
                </Grid> : <p>{t('folders:noSimilarImageColors')}</p>}
        </Results>}
    </>;
}
const Find = styled.button`
    display: inline-flex; align-items: center; gap: 8px; padding: 8px 14px;
    color: rgba(255,255,255,.9); background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.1);
    border-radius: 8px; font: inherit; font-size: 12px; cursor: pointer;
    transition: background 0.15s ease;
    &:hover { background: rgba(255,255,255,.065); }
    &:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }
    &:disabled { opacity: .5; }
`;
const Results = styled.section`
    order: 2; flex: 0 0 100%; max-height: 260px; overflow-y: auto; padding: 10px 0;
    p { font-size: 12px; color: rgba(255,255,255,.65); }
`;
const ResultHeader = styled.div`
    display: flex; justify-content: space-between; align-items: center; font-size: 12px; margin-bottom: 10px;
    button { color: inherit; background: none; border: 0; font-size: 20px; cursor: pointer; }
`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fill,minmax(90px,1fr)); gap: 8px;`;
const Result = styled.button`
    padding: 0; border: 0; background: rgba(255,255,255,.04); border-radius: 7px; overflow: hidden; cursor: pointer;
    img { display: block; width: 100%; height: 110px; object-fit: cover; }
    &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;

const Placeholder = styled.span`
    display: flex; height: 110px; padding: 12px; box-sizing: border-box; align-items: center;
    justify-content: center; flex-direction: column; gap: 8px; color: rgba(255,255,255,.65);
    span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; }
`;
